import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon, ChevronUpIcon, TrashIcon, MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import { fetchWithAuth } from '../../services/api';

const ViewAllUsers = () => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language;
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedUser, setExpandedUser] = useState(null);
    const [deletingUserId, setDeletingUserId] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [profileFilter, setProfileFilter] = useState('all');
    const [sortBy, setSortBy] = useState('newest');

    useEffect(() => {
        const fetchUsers = async () => {
            setLoading(true);
            setError(null);
            const token = localStorage.getItem('jwtToken');
            const role = localStorage.getItem('userRole');

            if (!token || role !== 'ADMIN') {
                navigate(`/${lang}/login`);
                return;
            }

            try {
                const data = await fetchWithAuth('/admin/users');
                setUsers(data);
            } catch (err) {
                console.error('Error fetching users:', err);
                setError(err.message || t('viewAllUsers.errorGeneric', 'An unexpected error occurred'));
            } finally {
                setLoading(false);
            }
        };

        fetchUsers();
    }, [navigate, t, lang]);

    const toggleUserExpansion = (userId) => {
        setExpandedUser(expandedUser === userId ? null : userId);
    };

    const handleDeleteClick = (userId, event) => {
        event.stopPropagation();
        setDeletingUserId(userId);
        setDeleteConfirmOpen(true);
        setDeleteError(null);
    };

    const confirmDelete = async () => {
        if (!deletingUserId) return;

        try {
            await fetchWithAuth(`/admin/users/${deletingUserId}`, {
                method: 'DELETE',
            });

            // Remove the deleted user from the list
            setUsers(users.filter(user => user.id !== deletingUserId));
            setDeleteConfirmOpen(false);
            setDeletingUserId(null);
        } catch (err) {
            console.error('Error deleting user:', err);
            setDeleteError(err.message || t('viewAllUsers.deleteError', 'Failed to delete user'));
        }
    };

    const cancelDelete = () => {
        setDeleteConfirmOpen(false);
        setDeletingUserId(null);
        setDeleteError(null);
    };

    const visibleUsers = useMemo(() => {
        const normalizedSearch = search.trim().toLocaleLowerCase();
        return users
            .filter(user => !normalizedSearch
                || user.fullName?.toLocaleLowerCase().includes(normalizedSearch)
                || user.email?.toLocaleLowerCase().includes(normalizedSearch))
            .filter(user => roleFilter === 'all' || user.role === roleFilter)
            .filter(user => statusFilter === 'all' || (statusFilter === 'enabled' ? user.enabled : !user.enabled))
            .filter(user => profileFilter === 'all'
                || (profileFilter === 'with' ? user.profiles?.length > 0 : !user.profiles?.length))
            .sort((left, right) => {
                if (sortBy === 'name') return (left.fullName || '').localeCompare(right.fullName || '');
                if (sortBy === 'oldest') return new Date(left.createdAt || 0) - new Date(right.createdAt || 0);
                return new Date(right.createdAt || 0) - new Date(left.createdAt || 0);
            });
    }, [users, search, roleFilter, statusFilter, profileFilter, sortBy]);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[calc(100vh-10rem)]">
                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-[calc(100vh-10rem)] px-4">
                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-6 rounded-md shadow-md max-w-md w-full">
                    <h3 className="font-bold text-lg mb-2">{t('viewAllUsers.errorTitle', 'Error')}</h3>
                    <p>{error}</p>
                    <button
                        onClick={() => { setLoading(true); setError(null); /* Call fetchUsers() or similar */ navigate(0);}}
                        className="mt-4 px-4 py-2 bg-red-500 text-white font-semibold rounded hover:bg-red-600 transition duration-150 ease-in-out"
                    >
                        {t('viewAllUsers.retry', 'Try Again')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="flex justify-between items-center mb-6">
                <h1
                  className="text-4xl font-extrabold text-gray-900"
                  data-testid="view-all-users-title"
                >
                    {t('viewAllUsers.title', 'User Management')}
                </h1>
            </div>

            <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(240px,1fr)_140px_140px_160px_160px]">
                    <label className="relative">
                        <span className="sr-only">{t('common.search', 'Search')}</span>
                        <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                        <input
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder={t('viewAllUsers.searchPlaceholder', 'Search by name or email')}
                            className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-3 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                        />
                    </label>
                    <select value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
                        <option value="all">{t('viewAllUsers.filters.allRoles', 'All roles')}</option>
                        <option value="USER">{t('viewAllUsers.filters.users', 'Users')}</option>
                        <option value="ADMIN">{t('viewAllUsers.filters.admins', 'Admins')}</option>
                    </select>
                    <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
                        <option value="all">{t('viewAllUsers.filters.allStatuses', 'All statuses')}</option>
                        <option value="enabled">{t('viewAllUsers.filters.enabled', 'Enabled')}</option>
                        <option value="disabled">{t('viewAllUsers.filters.disabled', 'Disabled')}</option>
                    </select>
                    <select value={profileFilter} onChange={(event) => setProfileFilter(event.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
                        <option value="all">{t('viewAllUsers.filters.allProfiles', 'Any profiles')}</option>
                        <option value="with">{t('viewAllUsers.filters.withProfiles', 'With profiles')}</option>
                        <option value="without">{t('viewAllUsers.filters.withoutProfiles', 'Without profiles')}</option>
                    </select>
                    <select value={sortBy} onChange={(event) => setSortBy(event.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
                        <option value="newest">{t('common.newestFirst', 'Newest first')}</option>
                        <option value="oldest">{t('common.oldestFirst', 'Oldest first')}</option>
                        <option value="name">{t('common.nameAZ', 'Name A–Z')}</option>
                    </select>
                </div>
                <p className="mt-3 text-xs text-gray-500">{visibleUsers.length} {t('viewAllUsers.results', 'results')}</p>
            </div>

            {visibleUsers.length === 0 ? (
                <div className="text-center py-10 bg-white shadow-xl rounded-lg">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">{t('viewAllUsers.noUsersFound', 'No Users Found')}</h3>
                    <p className="mt-1 text-sm text-gray-500">{t('viewAllUsers.noUsersDescription', 'There are currently no users in the system to display.')}</p>
                </div>
            ) : (
                <div className="bg-white shadow-xl rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-300">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 w-10"></th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                        {t('viewAllUsers.table.name', 'Name')}
                                    </th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                        {t('viewAllUsers.table.email', 'Email')}
                                    </th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                        {t('viewAllUsers.table.phone', 'Phone')}
                                    </th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                        {t('viewAllUsers.table.created', 'Created')}
                                    </th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                        {t('viewAllUsers.table.quota', 'Profili')}
                                    </th>
                                    <th scope="col" className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                                        {t('viewAllUsers.table.actions', 'Actions')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                                {visibleUsers.map((user) => (
                                    <React.Fragment key={user.id}>
                                        <tr
                                            className={`hover:bg-indigo-100 transition-colors duration-150 cursor-pointer ${expandedUser === user.id ? 'bg-indigo-50' : ''}`}
                                            onClick={() => navigate(`/${lang}/admin/user-details/${user.id}`)}
                                            data-testid={`user-row-${user.id}`}
                                        >
                                            <td className="py-4 pl-4 pr-3 text-sm sm:pl-6">
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); toggleUserExpansion(user.id); }}
                                                    className="text-indigo-600 hover:text-indigo-800 p-1 rounded-full hover:bg-indigo-200 transition-colors"
                                                >
                                                    {expandedUser === user.id ? <ChevronUpIcon className="h-5 w-5" /> : <ChevronDownIcon className="h-5 w-5" />}
                                                </button>
                                            </td>
                                            <td
                                              className="whitespace-nowrap px-3 py-4 text-sm text-gray-700 font-medium"
                                              data-testid="user-fullname-cell"
                                            >
                                              {user.fullName}
                                            </td>
                                            <td
                                              className="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                                              data-testid="user-email-cell"
                                            >
                                              {user.email}
                                            </td>
                                            <td
                                              className="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                                              data-testid="user-phone-cell"
                                            >
                                              {user.phone}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'}
                                            </td>
                                            <td
                                              className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center"
                                              data-testid="user-profiles-quota-cell"
                                            >
                                              {user.profiles ? user.profiles.length : 0}/{user.maxProfiles || 1}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
                                                <button
                                                    onClick={(e) => handleDeleteClick(user.id, e)}
                                                    className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-100 transition-colors"
                                                    title={t('viewAllUsers.deleteUser', 'Delete User')}
                                                    data-testid={`delete-user-${user.id}`}
                                                >
                                                    <TrashIcon className="h-5 w-5" />
                                                </button>
                                            </td>
                                        </tr>
                                        {expandedUser === user.id && (
                                            <tr className="bg-gray-50">
                                                <td colSpan={7} className="p-0">
                                                    <div className="mx-4 my-2 sm:mx-6 p-4 bg-indigo-50 rounded-lg shadow-inner">
                                                        <h4 className="text-md font-semibold text-gray-800 mb-3">
                                                            {t('viewAllUsers.profilesFor', { name: user.fullName })}
                                                        </h4>
                                                        {user.profiles && user.profiles.length > 0 ? (
                                                            <ul className="list-disc list-inside space-y-1 pl-2">
                                                                {user.profiles.map(profile => (
                                                                    <li key={profile.stringId || profile.id} className="text-sm text-gray-600">
                                                                        <Link to={`/${lang}/profile/${profile.stringId || profile.id}`} className="text-indigo-700 hover:text-indigo-900 hover:underline font-medium">
                                                                            {profile.name}
                                                                        </Link>
                                                                        <span className="text-xs text-gray-500"> (ID: {profile.stringId || profile.id})</span>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        ) : (
                                                            <p className="text-sm text-gray-500">{t('viewAllUsers.noProfilesForUser', 'This user has no profiles.')}</p>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirmOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                    <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={cancelDelete}></div>
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                            <div className="sm:flex sm:items-start">
                                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                    <TrashIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                                </div>
                                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                        {t('viewAllUsers.confirmDelete.title', 'Delete User')}
                                    </h3>
                                    <div className="mt-2">
                                        <p className="text-sm text-gray-500">
                                            {t('viewAllUsers.confirmDelete.message', 'Are you sure you want to delete this user? This action cannot be undone. All profiles, orders, and data associated with this user will be permanently deleted.')}
                                        </p>
                                        {deleteError && (
                                            <p className="mt-2 text-sm text-red-600">{deleteError}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                                <button
                                    type="button"
                                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={confirmDelete}
                                >
                                    {t('viewAllUsers.confirmDelete.confirm', 'Delete')}
                                </button>
                                <button
                                    type="button"
                                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                                    onClick={cancelDelete}
                                >
                                    {t('viewAllUsers.confirmDelete.cancel', 'Cancel')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ViewAllUsers;
